import React, { useEffect, useRef, useState } from "react";
import "./QRCode.scss";

/**
 * QR Code Component
 * 
 * Generates a QR code for the provided URL using qr-code-styling library.
 * The QR code is clickable and links to the specified URL.
 * 
 * @param {string} url - URL to encode in QR code (supports deep links or smart links)
 */
const QRCode = ({ url = "dusk.app/download" }) => {
  const qrRef = useRef(null);
  const [error, setError] = useState(null);

  // Format URL for href - add https:// if it's not a deep link or already has a protocol
  const getClickableUrl = () => {
    if (!url) return "#";
    // If it's a deep link (starts with dusk:// or similar) or already has protocol, use as-is
    if (url.includes("://")) {
      return url;
    }
    // Otherwise, add https://
    return `https://${url}`;
  };

  useEffect(() => {
    let isMounted = true;

    const loadQRCode = async () => {
      try {
        const qrCodeModule = await import("qr-code-styling");
        const QRCodeStyling = qrCodeModule.default || qrCodeModule;

        if (!isMounted || !qrRef.current) return;

        const qrCode = new QRCodeStyling({
          width: 300,
          height: 300,
          type: "svg",
          data: url,
          dotsOptions: {
            color: "#ffffff",
            type: "rounded"
          },
          backgroundOptions: {
            color: "#1b1b1b", // Same grey as bounding box
          },
          cornersSquareOptions: {
            color: "#ffffff",
            type: "extra-rounded"
          },
          cornersDotOptions: {
            color: "#ffffff",
            type: "dot"
          }
        });

        if (qrRef.current) {
          qrRef.current.innerHTML = "";
          qrCode.append(qrRef.current);
        }
      } catch (err) {
        if (!isMounted) return;
        setError(true);
      }
    };

    loadQRCode();

    return () => {
      isMounted = false;
    };
  }, [url]);

  if (error) {
    return null;
  }

  return (
    <a
      href={getClickableUrl()}
      className="qr-code-container"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Scan QR code or click to open link"
    >
      <div ref={qrRef} className="qr-code"></div>
    </a>
  );
};

export default QRCode;
